import React, { useState } from 'react';
import { RefreshCw, CheckCircle } from 'lucide-react';
import './BoardDeckLeftPanel.css';

const BoardDeckLeftPanel = ({
  mode,
  setMode,
  tone,
  setTone,
  targetAudience,
  setTargetAudience,
  additionalInstructions,
  setAdditionalInstructions,
  targetWordCount,
  setTargetWordCount,
  selectedFiles,
  availableFiles,
  onFileToggle,
  onGenerate,
  isGenerating
}) => {
  const [showFileSelector, setShowFileSelector] = useState(false);

  const toneOptions = [
    { value: 'formal', label: 'Formal' },
    { value: 'executive', label: 'Executive' },
    { value: 'analytical', label: 'Analytical' }
  ];

  const audienceOptions = [
    { value: 'analysts', label: 'Analysts' },
    { value: 'investors', label: 'Investors' },
    { value: 'regulators', label: 'Regulators' },
    { value: 'board_of_directors', label: 'Board of Directors' },
    { value: 'internal_team', label: 'Internal Team' }
  ];

  return (
    <div className="board-deck-left-panel">
      {/* Header */}
      <div className="panel-header">
        <h1 className="panel-title">Board Deck Drafting Assistant</h1>
        <p className="panel-description">
          Generate narrative for board presentation
        </p>
      </div>

      {/* Input Method Tabs */}
      <div className="input-method-tabs">
        <button 
          className={`tab ${mode === 'auto' ? 'active' : ''}`}
          onClick={() => setMode('auto')}
        >
          Auto
        </button>
        <button 
          className={`tab ${mode === 'manual' ? 'active' : ''}`}
          onClick={() => setMode('manual')}
        >
          Manual
        </button>
      </div>

      {/* Configuration Sections */}
      <div className="config-sections">
        {/* Tone Selection */}
        <div className="config-section">
          <div className="config-label">Tone</div>
          <div className="config-buttons">
            {toneOptions.map((option) => (
              <button
                key={option.value}
                className={`config-button ${tone === option.value ? 'active' : ''}`}
                onClick={() => setTone(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Target Audience Selection */}
        <div className="config-section">
          <div className="config-label">Target Audience</div>
          <div className="config-buttons">
            {audienceOptions.map((option) => (
              <button
                key={option.value}
                className={`config-button ${targetAudience === option.value ? 'active' : ''}`}
                onClick={() => setTargetAudience(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* File Selection (Manual Mode Only) */}
        {mode === 'manual' && (
          <div className="config-section">
            <div className="config-label">File Selection</div>
            <button
              className="file-selector-btn"
              onClick={() => setShowFileSelector(!showFileSelector)}
            >
              {showFileSelector ? 'Hide Files' : 'Select Files'} ({selectedFiles.length})
            </button>
            
            {showFileSelector && (
              <div className="file-selector">
                <div className="file-list">
                  {availableFiles.map(file => (
                    <div
                      key={file.id}
                      className={`file-item ${selectedFiles.includes(file.id) ? 'selected' : ''}`}
                      onClick={() => onFileToggle(file.id)}
                    >
                      <div className="file-info">
                        <div className="file-name">{file.display_name}</div>
                        <div className="file-details">
                          {file.bank} • {file.year} {file.quarter} • {file.document_type}
                        </div>
                      </div>
                      <div className="file-checkbox">
                        {selectedFiles.includes(file.id) && <CheckCircle size={12} />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Additional Instructions */}
        <div className="config-section">
          <div className="config-label">Additional Instructions</div>
          <textarea
            className="instructions-input"
            value={additionalInstructions}
            onChange={(e) => setAdditionalInstructions(e.target.value)}
            placeholder="E.g., Focus on retail segment growth, emphasize digital transformation..."
            rows={3}
          />
        </div>

        {/* Target Word Count */}
        <div className="config-section">
          <div className="config-label">Target Word Count</div>
          <input
            type="number"
            className="word-count-input"
            value={targetWordCount}
            onChange={(e) => setTargetWordCount(parseInt(e.target.value) || 1000)}
            min={100}
            max={5000}
            step={100}
          />
        </div>
      </div>

      {/* Generate Button */}
      <button 
        className="generate-button"
        onClick={onGenerate}
        disabled={isGenerating || (mode === 'manual' && selectedFiles.length === 0)}
      >
        <RefreshCw size={16} />
        {isGenerating ? 'Generating...' : 'Generate Board Deck'}
      </button>
    </div>
  );
};

export default BoardDeckLeftPanel;

